import { Button } from '@/components/Button';
import { Form } from '@/components/Form';
import { Flex } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import { Input } from '@/components/Input';
import { PostalCodeField } from '@/components/PostalCodeField';
import { Text } from '@/components/Text';
import { Textarea } from '@/components/Textarea';
import { type AdType } from '@/resource/ad';
import { type LocationItemType } from '@/resource/location';
import { type SetState } from '@/util/types';
import { CancelLink } from './CancelLink';

type Props = {
  isValid: boolean;
  adType: AdType;
  title: string;
  setTitle: SetState<string>;
  description: string;
  setDescription: SetState<string>;
  postalCode: string;
  setPostalCode: SetState<string>;
  useGeneratedTitle: boolean;
  location: LocationItemType | null;
  setLocation: (value: LocationItemType | null) => void;
  onBack?: () => void;
  onNext: () => void;
};

export function AdInfoForm({
  isValid,
  adType,
  title,
  setTitle,
  description,
  setDescription,
  postalCode,
  setPostalCode,
  useGeneratedTitle,
  location,
  setLocation,
  onBack,
  onNext,
}: Props) {
  const label = adType === 'offer' ? 'aanbod' : 'vraag';

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
    >
      <Flex dir="column" gap={4}>
        {useGeneratedTitle ? (
          <>
            <Heading visualLevel={3} align="center">
              Locatie
            </Heading>
            <Text>
              Gebruik je eigen locatie of geef optioneel een andere locatie aan.
            </Text>
          </>
        ) : adType === 'offer' ? (
          <Heading visualLevel={3} align="center">
            Omschrijf jouw aanbod
          </Heading>
        ) : (
          <>
            <Heading visualLevel={3} align="center">
              Waar heb je hulp bij nodig?
            </Heading>
            <Text>
              Plaats jouw vraag hier en kom veilig in contact met mensen die
              iets voor je kunnen beteken. Omschrijf zo duidelijk mogelijk wat
              je zoekt.
            </Text>
          </>
        )}

        {!useGeneratedTitle && (
          <>
            <Input
              label="Titel"
              required
              name="title"
              id="title"
              value={title}
              maxLength={45}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              label="Omschrijving"
              required
              name="description"
              id="description"
              value={description}
              placeholder={
                adType === 'request'
                  ? 'Omschrijf je vraag zo duidelijk mogelijk. Leg uit waarom je hulp nodig hebt en wat er precies moet gebeuren.'
                  : ''
              }
              rows={5}
              maxLength={512}
              onChange={(e) => setDescription(e.target.value)}
            />
          </>
        )}

        <PostalCodeField
          location={location}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          setLocation={setLocation}
        />

        {!useGeneratedTitle && (
          <Text>
            Je {label} is voor iedereen zichtbaar, vermeld geen adresgegevens of
            telefoonnummers; dit kan later in een privébericht.
          </Text>
        )}

        <Flex justify={onBack ? 'space-between' : 'end'}>
          {onBack && (
            <Button
              theme="secondary"
              icon="chevron_left_narrow"
              iconSize={2}
              onClick={onBack}
            >
              Terug
            </Button>
          )}

          {useGeneratedTitle ? (
            <Button
              type="submit"
              icon="chevron_right_narrow"
              iconPosition="right"
              iconSize={2}
              disabled={!isValid}
            >
              Naar activiteiten
            </Button>
          ) : (
            <Button
              type="submit"
              icon="chevron_right_narrow"
              iconPosition="right"
              iconSize={2}
              disabled={!isValid}
            >
              Naar categorie
            </Button>
          )}
        </Flex>

        <CancelLink confirm adType={adType} />
      </Flex>
    </Form>
  );
}
